import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import { NineBoxGrid, NB_DISPOSITIONS, TIERS, tierLabel } from "@/components/blueprints/NineBoxBlueprint";

const uid = () => "nb_" + Math.random().toString(36).slice(2, 10);
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";
const selCls = "h-10 rounded-sm border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

export default function NineBox() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setContext(res.data.payload?.context || ""); setPeople(res.data.payload?.people || []); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled 9-Box Matrix", payload: { context, people }, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, context, people]);

  const addPerson = () => setPeople((x) => [...x, { id: uid(), name: "", role: "", performance: "med", potential: "med", disposition: "" }]);
  const setPerson = (i, patch) => setPeople((x) => x.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const removePerson = (i) => setPeople((x) => x.filter((_, j) => j !== i));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Matrix finalised"); navigate(`/ninebox/${id}/view`); } };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="ninebox-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="9-Box matrix title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <textarea data-testid="ninebox-context-input" rows={2} value={context} onChange={(e) => setContext(e.target.value)} placeholder="Context — team, review cycle or succession question…" className="mt-3 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" />

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><h2 className="text-xl font-semibold text-slate-900">Team Members</h2><p className="text-sm text-slate-500">Tag each person's performance and potential — they map onto the grid instantly.</p></div>
            <button data-testid="ninebox-add-person" onClick={addPerson} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add person</button>
          </div>
          {people.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add team members to begin.</p>}
          <div className="space-y-2">
            {people.map((p, i) => (
              <div key={p.id} data-testid={`ninebox-person-${i}`} className="flex flex-col lg:flex-row lg:items-center gap-2 border border-slate-200 rounded-sm p-3">
                <input data-testid={`ninebox-name-${i}`} className={inputCls + " lg:w-44"} value={p.name} onChange={(e) => setPerson(i, { name: e.target.value })} placeholder="Name" />
                <input data-testid={`ninebox-role-${i}`} className={inputCls + " lg:w-40"} value={p.role} onChange={(e) => setPerson(i, { role: e.target.value })} placeholder="Role" />
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-slate-400">Perf</span>
                  <select data-testid={`ninebox-performance-${i}`} className={selCls} value={p.performance} onChange={(e) => setPerson(i, { performance: e.target.value })}>
                    {TIERS.map((t) => <option key={t} value={t}>{tierLabel[t]}</option>)}
                  </select>
                  <span className="text-xs font-mono text-slate-400">Pot</span>
                  <select data-testid={`ninebox-potential-${i}`} className={selCls} value={p.potential} onChange={(e) => setPerson(i, { potential: e.target.value })}>
                    {TIERS.map((t) => <option key={t} value={t}>{tierLabel[t]}</option>)}
                  </select>
                </div>
                <select data-testid={`ninebox-disposition-${i}`} className={selCls + " flex-1"} value={p.disposition} onChange={(e) => setPerson(i, { disposition: e.target.value })}>
                  <option value="">Disposition path…</option>
                  {NB_DISPOSITIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <button data-testid={`ninebox-remove-${i}`} onClick={() => removePerson(i)} className="p-2 text-slate-400 hover:text-red-600 self-end lg:self-auto"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <h2 className="text-sm font-mono font-bold uppercase tracking-[0.1em] text-slate-700 mb-4">Live Grid</h2>
          <NineBoxGrid people={people} />
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="ninebox-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
          <button data-testid="ninebox-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Matrix</button>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase="PEOPLE" />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="nine-box-matrix" step={{ label: "9-Box Matrix" }} phase="PEOPLE" onApplySuggestion={(t) => { navigator.clipboard?.writeText(t); toast.info("Copied to clipboard"); }} />
    </Layout>
  );
}
